import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  MutableRefObject,
  useCallback,
} from 'react';
import _ from 'lodash';
import { IAppState } from 'spw-redux/reducer';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { pathOr } from 'ramda';
import * as constants from 'spw-constants';
import { MACRO_PROGRAMMING_TYPE } from 'spw-constants';
import { SPWExportButton } from 'spw-shared/globalComponents';
import { JustifyEndRow } from 'spw-shared/globalComponents/grid';
import { IUser } from 'spw-redux/modules/user/types';
import { Category } from 'spw-redux/modules/space_types/types';
import {
  IBuilding,
  IProject,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import { IProjectSettings } from 'spw-redux/modules/settings/types';
import { setProjectSettings as setProjectSettingsAction } from 'spw-redux/modules/settings/actions';
import ProgramFileCabinetsLockersRatioModal from 'spw-components/dialogs/program-file-cabinets-locker-ratios';
import { Menu } from '@mui/material';
import NoteAdd from '@mui/icons-material/NoteAdd';
import Settings from '@mui/icons-material/Settings';
import {
  fetchCurrentWorksheet,
  setWorksheetNotesDialogStatus,
} from 'spw-redux/modules/calculators/actions';
import { updateProjectSpaceTypeCategory } from 'spw-redux/modules/project-space-type-categories/actions';
import WhiteLink from 'spw-static/images/white-link.svg';
import WorksheetTooltipModal from 'spw-components/worksheet/worksheet-tooltip';
import { isScenarioLinked } from 'spw-components/growth-calculator/total-program-utils';
import { Gripper } from 'spw-components/global/Gripper';
import OpenCloseIcon from 'spw-static/images/open-close-icon.svg';
import ReassignSpaceTypesDialog from 'spw-components/dialogs/spaces/reassign-space-types-dialog';
import RemoveSpaceTypeDialog from 'spw-components/dialogs/worksheet/remove-space-type';
import UpdateSpaceTypeCategoryDialog from 'spw-components/dialogs/update-space-category';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import {
  selectCurrentScenario,
  selectTotalPrograms,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import { colors } from 'spw-theme';
import { useTrackWorksheetActions } from 'spw-shared/hooks/use-track-worksheet-actions';
import { OptionsButton } from 'spw-projects/components/worksheet-styles';
import {
  Container,
  Content,
  Header,
  MacroMenuItem,
  NotesButton,
} from './styles';
import { useWorksheetResize } from './utils/hooks';
import { PlainProgramScenario } from '../../models/Scenario';
import { PlainWorksheet } from '../../models/Worksheet';
import WorksheetDownloadsDialog from './worksheet-downloads-dialog';

export type SpaceTypesRefRecords = Record<
  Category,
  MutableRefObject<HTMLDivElement> | null
>;

interface IProps {
  project: IProject;
  children: any;
  snakeProjectType: string;
  user?: IUser;
  scenario: PlainProgramScenario;
  worksheet: PlainWorksheet;
  building: IBuilding;
  individualReportZipName: null | string;
  totalPrograms: PlainProgramScenario[];
  projectSettings: IProjectSettings;
  setProjectSettings: (projectSettings: IProjectSettings) => void;
  showRemoveSpaceTypeDialog: boolean;
  optionsAreDisabled?: boolean;
  isDownloadingIndividualReport?: boolean;
  clientFacingNamesAreVisible?: boolean;
  showDecimals?: boolean;
}

const WorksheetContainer: React.FC<IProps> = ({
  project,
  children,
  snakeProjectType,
  scenario,
  individualReportZipName,
  totalPrograms,
  projectSettings,
  setProjectSettings,
  showRemoveSpaceTypeDialog,
  optionsAreDisabled,
  isDownloadingIndividualReport,
  clientFacingNamesAreVisible,
}) => {
  const dispatch = useDispatch();
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [ratioCalcsOpen, setRatioCalcsOpen] = useState(false);
  const [showReassignSpaceType, setReassignSpaceType] = useState(false);
  const [downloadOptionsAreVisible, setDownloadOptionsAreVisible] = useState(
    false
  );
  const [
    isCreateSubcategoryDialogOpen,
    setIsCreateSubcategoryDialogOpen,
  ] = useState(false);
  const cref = useRef(null);
  const href = useRef(null);
  const contentRef = useRef(null);
  const { worksheetHeight, openOrCloseWorksheet } = useWorksheetResize(
    href,
    cref,
    snakeProjectType as typeof constants.PROGRAMMING
  );
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);

  const isMacro = scenario?.calculatorType === MACRO_PROGRAMMING_TYPE;
  const { handleClick } = useTrackWorksheetActions(
    snakeProjectType,
    contentRef
  );

  useEffect(() => {
    if (scenario?.areStoredWorkSeatsOutdated) openOrCloseWorksheet();
  }, [scenario?.areStoredWorkSeatsOutdated, openOrCloseWorksheet]);

  const openWorksheetNotesDialog = () =>
    dispatch(setWorksheetNotesDialogStatus(true));

  const isTotalProgram =
    scenario?.calculatorType === constants.TOTAL_PROGRAM_TYPE;

  const individualWorkspacesCategory = useMemo(() => {
    return project.spaceTypeCategories.find((spaceTypeCategory) => {
      return (
        spaceTypeCategory.defaultSpaceTypeCategory.category ===
        constants.INDIVIDUAL_WORKSPACES_CATEGORY
      );
    });
  }, [project.spaceTypeCategories]);

  const doesProjectHaveCustomSubcategory = useMemo(() => {
    return individualWorkspacesCategory.subcategories.some(
      (stc) =>
        stc.defaultSpaceTypeCategory.category === constants.CUSTOM_SUBCATEGORY
    );
  }, [individualWorkspacesCategory]);

  const handleExportButtonClick = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setDownloadOptionsAreVisible(true);
  };

  const handleOptionsButtonClick = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setOptionsAnchorEl(event.currentTarget);
  };

  const isLinked = isScenarioLinked(scenario?.id, totalPrograms);

  const onCloseReassignSpaceTypeDialog = useCallback(() => {
    setReassignSpaceType(false);
  }, []);

  const showCreateSubcategoryDialog = () => {
    setOptionsAnchorEl(null);
    setIsCreateSubcategoryDialogOpen(true);
  };

  const closeCreateSubcategoryDialog = () => {
    setIsCreateSubcategoryDialogOpen(false);
  };

  const updateIndividualWorkspacesCategory = (
    updatedSpaceTypeCategory: IProjectSpaceTypeCategory
  ) => {
    dispatch(
      updateProjectSpaceTypeCategory(updatedSpaceTypeCategory, () => {
        dispatch(fetchCurrentWorksheet());
      })
    );
    setIsCreateSubcategoryDialogOpen(false);
  };

  if (!scenario) return null;

  return (
    <>
      <ProgramFileCabinetsLockersRatioModal
        projectSettings={projectSettings}
        setProjectSettings={setProjectSettings}
        open={ratioCalcsOpen}
        onClose={() => setRatioCalcsOpen(false)}
        headcount={scenario.headcount}
        workSeats={scenario.workSeats}
        headcountWithUtilization={scenario.headcountWithUtilization}
        headcountWithVacancy={scenario.headcountWithVacancy}
      />
      {isCreateSubcategoryDialogOpen && (
        <UpdateSpaceTypeCategoryDialog
          project={project}
          spaceTypeCategory={individualWorkspacesCategory}
          spaceTypeCategories={project.spaceTypeCategories}
          defaultConfig={constants.DEFAULT_SPACE_TYPE_CATEGORIES.find(
            (defaultSpaceTypeCategory) =>
              individualWorkspacesCategory?.defaultSpaceTypeCategory
                ?.category === defaultSpaceTypeCategory.category
          )}
          open={isCreateSubcategoryDialogOpen}
          onCancel={closeCreateSubcategoryDialog}
          onSave={updateIndividualWorkspacesCategory}
        />
      )}
      <Container
        ref={cref}
        style={{ height: worksheetHeight }}
        onClick={handleClick}
        data-cy="worksheet_container"
      >
        {showRemoveSpaceTypeDialog && <RemoveSpaceTypeDialog />}

        <div
          style={{ display: 'flex', flexDirection: 'column' }}
          ref={href}
          data-cy="gripper_wrapper"
        >
          {showReassignSpaceType && (
            <ReassignSpaceTypesDialog
              showDialog={showReassignSpaceType}
              handleOnClose={onCloseReassignSpaceTypeDialog}
              clientFacingNamesAreVisible={clientFacingNamesAreVisible}
            />
          )}
          <Gripper />
          <Header>
            <span
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
              data-testid="worksheet_title"
              aria-label="Worksheet Title"
            >
              {scenario?.name}
              {(isTotalProgram || isLinked) && (
                <div
                  style={{
                    marginLeft: '8px',
                    marginTop: '4px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <img
                    src={WhiteLink}
                    alt="white link"
                    data-testid="worksheet_header_link_icon"
                  />
                </div>
              )}
            </span>
            <JustifyEndRow>
              {(!isMacro || !scenario.isShared) && !isTotalProgram && (
                <WorksheetTooltipModal
                  disableButton={optionsAreDisabled}
                  projectSpaceTypeCategories={project.spaceTypeCategories}
                />
              )}
              <NotesButton
                data-testid="notes_button"
                startIcon={<NoteAdd style={{ fontSize: '14px' }} />}
                onClick={openWorksheetNotesDialog}
                disabled={optionsAreDisabled}
                style={{ color: colors.white }}
              >
                <span style={{ color: colors.white }}>Notes</span>
              </NotesButton>
              {!isTotalProgram && (
                <>
                  <OptionsButton
                    startIcon={<Settings style={{ fontSize: '14px' }} />}
                    onClick={
                      isDownloadingIndividualReport
                        ? () => Function.prototype
                        : handleOptionsButtonClick
                    }
                    disabled={optionsAreDisabled || currentUserIsViewOnly}
                    data-cy="worksheet-options-button"
                  >
                    <span style={{ color: colors.white }}>Options</span>
                  </OptionsButton>
                  <Menu
                    id="download-worksheet-menu"
                    anchorEl={optionsAnchorEl}
                    open={Boolean(optionsAnchorEl)}
                    onClose={() => setOptionsAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    disableScrollLock
                  >
                    <MacroMenuItem
                      onClick={showCreateSubcategoryDialog}
                      key="edit-iw-space-category-option"
                      data-cy="edit-iw-space-category-option"
                    >
                      {doesProjectHaveCustomSubcategory
                        ? 'Edit Subcategory'
                        : 'Create a Subcategory'}
                    </MacroMenuItem>
                    <MacroMenuItem
                      onClick={() => {
                        setOptionsAnchorEl(null);
                        setRatioCalcsOpen(true);
                      }}
                      key="all-worksheet-rows-open-pdf"
                      data-cy="adjust-ratio-calcs-option"
                    >
                      Adjust Ratio Calculations
                    </MacroMenuItem>
                    <MacroMenuItem
                      key="reassign-a-space-type"
                      onClick={() => {
                        setOptionsAnchorEl(null);
                        setReassignSpaceType(true);
                      }}
                    >
                      Reassign a Space Type
                    </MacroMenuItem>
                  </Menu>
                </>
              )}
              <SPWExportButton
                color="white"
                classes="smaller"
                style={{ padding: '0 22px' }}
                busy={
                  isDownloadingIndividualReport &&
                  individualReportZipName?.includes('Worksheet')
                }
                disabled={optionsAreDisabled}
                onClick={
                  isDownloadingIndividualReport
                    ? () => Function.prototype
                    : handleExportButtonClick
                }
                testingPrefix="worksheet_container"
              />
              <WorksheetDownloadsDialog
                isVisible={downloadOptionsAreVisible}
                onClose={() => setDownloadOptionsAreVisible(false)}
                onExited={() => setDownloadOptionsAreVisible(false)}
              />

              <div
                style={{
                  width: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: `1px solid ${colors.greyInputAddornment}`,
                  height: '36px',
                  cursor: 'pointer',
                }}
                aria-hidden="true"
                onMouseDown={openOrCloseWorksheet}
                data-testid="worksheet_open_close"
              >
                <img src={OpenCloseIcon} alt="open-close icon" />
              </div>
            </JustifyEndRow>
          </Header>
        </div>
        <Content ref={contentRef}>{children}</Content>
      </Container>
    </>
  );
};

const mapStateToProps = (state: IAppState) => ({
  project: state.projectsReducer.currentProject,
  currentFloor: state.projectsReducer.currentFloor,
  scenario: selectCurrentScenario(state),
  worksheet: selectWorksheet(state),
  snakeProjectType: state.projectsReducer.currentProjectType,
  user: state.userReducer.user,
  building: pathOr(
    null,
    ['projectsReducer', 'currentProject', 'buildings', 0],
    state
  ),
  totalPrograms: selectTotalPrograms(state),
  projectSettings: state.settingsReducer.projectSettings,
  showRemoveSpaceTypeDialog: state.worksheetReducer.showRemoveSpaceTypeDialog,
  projectSpaceTypes: state.spaceTypesReducer.spaceTypes,
  individualReportZipName: state.downloadsReducer.zipName,
  isDownloadingIndividualReport:
    state.downloadsReducer.isDownloadingIndividualReport,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      setProjectSettings: setProjectSettingsAction,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(WorksheetContainer);
